import { MESSAGE_TYPE, CRM_ACTIONS_MAP, ERROR_CODE_MAP } from './constant';
import { RCXLogger } from './logger';
import {
    CRM_ACTIONS,
    MessageEventData,
    OnNewCall,
    RecordCond,
    OnMatchRecord,
    CallBase,
    OpenRecord,
    PopRecords,
    OnDispCall,
    OnEndCall,
    CrmMetadata,
    RecordResponse,
    Ticket,
    GetCallLinkResult,
    GetChatLinkResult,
    OnSubmitLog,
    ChatBase,
    OnChatDispResponse,
    OnGetCallLink,
    OnGetChatLink,
    OnUpdateRender,
    OnStopRender,
    OnStartRender,
    RCXMetadata,
} from './typing';

type HandlerFun = (data: any) => Promise<any> | any;
interface HandlerOptions {
    timeout: number;
}
export class RCXAdapter {
    rcxWindow?: Window;
    rcxWindowURL?: URL;
    integrationAppWindow?: Window;
    handlers: Map<CRM_ACTIONS, HandlerFun[]>;
    defaultTimeout = 10000;
    handlerOptions: Map<CRM_ACTIONS, HandlerOptions>;
    static instance(): RCXAdapter {
        if (!RCXAdapter.singleton) {
            RCXAdapter.singleton = new RCXAdapter();
        }
        return RCXAdapter.singleton;
    }

    static singleton: RCXAdapter | null = null;

    constructor() {
        this.handlers = new Map();
        this.handlerOptions = new Map([
            [CRM_ACTIONS.START_RENDER, { timeout: 180 * 1000 }],
            [CRM_ACTIONS.GET_MATCHING_RECORDS, { timeout: 15 * 1000 }],
        ]);
    }
    public init(rcxIFrame?: HTMLIFrameElement, rcxWindow?: Window) {
        this.rcxWindow = rcxIFrame?.contentWindow || rcxWindow;
        this.rcxWindowURL = rcxIFrame?.src ? new URL(rcxIFrame.src) : undefined;
        this.integrationAppWindow = window;
        if (!this.rcxWindow || !this.integrationAppWindow) {
            throw Error('RCXAdapter init failed!');
        }
        this._bindListeners();
    }

    private _bindListeners() {
        this.integrationAppWindow?.addEventListener('message', (event: { data: MessageEventData<any> }) => {
            const { type, action } = event.data;
            if (type === MESSAGE_TYPE) {
                this.processRCXEvent(event.data);
                if (Array.isArray(this.handlers.get(action))) {
                    this.handlers.get(action)?.forEach((handler) => this.wrapperHandler(event.data, handler));
                }
            }
        });
    }

    private wrapperHandler(event: MessageEventData<any>, handler: HandlerFun) {
        const { action, data } = event;
        const requestId = event.requestId || data?.requestId;
        const responseAction = CRM_ACTIONS_MAP[action];
        if (responseAction === undefined) {
            return handler(data);
        }
        const options = this.handlerOptions.get(action);
        return Promise.race([
            handler(data),
            new Promise((resolve, reject) => {
                setTimeout(() => {
                    reject(new Error(`Action ${action}-${requestId} timeout`));
                }, options?.timeout || this.defaultTimeout);
            }),
        ])
            .then((result) => {
                if (result !== undefined) {
                    this._sendViaIframeMessage(responseAction, result, requestId);
                }
            })
            .catch((err) => {
                // @ts-ignore
                window[Symbol.for('rcx_sentry')]?.captureException({
                    requestAction: action,
                    responseAction,
                    timeout: options?.timeout || this.defaultTimeout,
                    error: err,
                });
                this._sendViaIframeMessage(
                    responseAction,
                    {
                        success: false,
                        error: {
                            code: err?.code || ERROR_CODE_MAP.COMMON,
                            message: err?.message || 'Unknown Error',
                        },
                    },
                    requestId
                );
            });
    }

    private processRCXEvent(event: MessageEventData<any>) {
        const { action, data } = event;
        switch (action) {
            case CRM_ACTIONS.GET_CRM_LOCALE:
                this.trackAppVersion(data);
                break;
            case CRM_ACTIONS.AGENT_LOGIN:
                this.trackAgentInfo(data);
                break;
            default:
                break;
        }
    }

    private trackAgentInfo(data: any) {
        if (data?.agentSettings?.agentId) {
            // @ts-ignore
            window[Symbol.for('rcx_sentry')]?.setUser({
                id: String(data?.agentSettings?.agentId),
                accountId: String(data?.agentSettings?.accountId),
            });
            // @ts-ignore
            window[Symbol.for('rcx_sentry')]?.captureMessage('RCX Agent Login', 'info');
            RCXLogger.getInstance().setUserConfig(data?.agentSettings);
        }
    }

    private trackAppVersion(data: any) {
        const versions = {
            rcxBaseUrl: this.rcxWindowURL?.origin || 'unknown',
            rcxVersion: data?.rcxVersion || 'unknown',
            rcxCrmPlatform: this.rcxWindowURL?.searchParams.get('rcx_client_app_type'),
            rcxCrmVersion: this.rcxWindowURL?.searchParams.get('rcx_client_app_version'),
        };
        // @ts-ignore
        window[Symbol.for('rcx_sentry')]?.setTags(versions);
        // @ts-ignore
        window[Symbol.for('rcx_sentry')]?.captureMessage(`RCX initialized: ` + JSON.stringify(versions), 'info');
    }

    public sendScriptCallbacks(data: any) {
        this._sendViaIframeMessage(CRM_ACTIONS.SCRIPT_CALLBACK, data);
    }
    public showErrorMessage(data: any) {
        this._sendViaIframeMessage(CRM_ACTIONS.SHOW_ERROR_BANNER, data);
    }

    private _sendViaIframeMessage(action: CRM_ACTIONS, data?: any, requestId?: string) {
        this.rcxWindow?.postMessage(
            {
                type: MESSAGE_TYPE,
                requestId,
                action,
                data,
            },
            '*'
        );
    }
    private _addHandler(action: CRM_ACTIONS, handler: HandlerFun) {
        let handlerList = this.handlers.get(action);
        if (!Array.isArray(handlerList)) {
            this.handlers.set(action, []);
            handlerList = this.handlers.get(action);
        }
        handlerList!.push(handler);
    }
    // functions CRM -> RCX
    public suggestDial(phoneNumber: string) {
        this._sendViaIframeMessage(CRM_ACTIONS.SUGGEST_DIAL, {
            number: phoneNumber,
        });
    }

    public cancelSuggestDial() {
        this._sendViaIframeMessage(CRM_ACTIONS.CANCEL_SUGGEST_DIAL);
    }

    public dialToCall(phoneNumber: string, params?: Record<string, any>) {
        this._sendViaIframeMessage(CRM_ACTIONS.DIAL, {
            number: phoneNumber,
            params,
        });
    }

    public sendIntegrateInformation(data: CrmMetadata) {
        this._sendViaIframeMessage(CRM_ACTIONS.SEND_METADATA, {
            ...data,
            version: this.rcxWindowURL?.searchParams.get('rcx_client_app_version'),
        });
    }

    public analyticsTrack(event: string, data?: any) {
        this._sendViaIframeMessage(CRM_ACTIONS.ANALYTICS_TRACK, {
            event,
            data: {
                ...data,
            },
        });
    }

    public setCallLink(data: GetCallLinkResult) {
        this._sendViaIframeMessage(CRM_ACTIONS.SET_CALL_LINK, data);
    }
    public setScriptResult(data: any) {
        this._sendViaIframeMessage(CRM_ACTIONS.SET_SCRIPT_RESULT, data);
    }
    public getScriptData(data: any) {
        this._sendViaIframeMessage(CRM_ACTIONS.GET_SCRIPT_DATA, data);
    }
    public sendDispositionResult(data: any) {
        this._sendViaIframeMessage(CRM_ACTIONS.DISPOSITION_RESULT, data);
    }

    public signOutAgent() {
        this._sendViaIframeMessage(CRM_ACTIONS.SIGN_OUT_AGENT);
    }

    public updateRCXState(data: any) {
        this._sendViaIframeMessage(CRM_ACTIONS.UPDATE_STATE, data);
    }

    // Generic function to send locale information from CRM to RCX
    public sendLocaleInfoToRCX(locale: string) {
        this._sendViaIframeMessage(CRM_ACTIONS.SET_CRM_LOCALE, {
            locale,
        });
    }

    // listeners RCX --> CRM
    public onNewCall(handler: (data: OnNewCall) => void) {
        this._addHandler(CRM_ACTIONS.CALL_NEW_CALL, handler);
    }
    public onEndCall(handler: (data: OnEndCall) => void) {
        this._addHandler(CRM_ACTIONS.CALL_END_CALL, handler);
    }
    public onPendingDisposition(handler: (data: OnDispCall) => void) {
        this._addHandler(CRM_ACTIONS.CALL_PENDING_DISP, handler);
    }
    public onScriptCallback(handler: (data: any) => void) {
        this._addHandler(CRM_ACTIONS.SCRIPT_CALLBACK, handler);
    }

    public onSearchRecord(handler: (data: RecordCond) => Promise<RecordResponse>) {
        this._addHandler(CRM_ACTIONS.GET_SEARCH_RECORD, handler);
    }

    public onMatchingRecords(handler: (data: OnMatchRecord) => Promise<RecordResponse> | undefined) {
        this._addHandler(CRM_ACTIONS.GET_MATCHING_RECORDS, handler);
    }

    public onMatchingTickets(handler: (userId: string) => Promise<Ticket[]>) {
        this._addHandler(CRM_ACTIONS.GET_MATCHING_TICKETS, handler);
    }

    public onCallIn(handler: (data: CallBase) => void) {
        this._addHandler(CRM_ACTIONS.SIP_CALL_ANSWERED, handler);
    }

    public onCallRejected(handler: (data: CallBase) => void) {
        this._addHandler(CRM_ACTIONS.SIP_CALL_REJECTED, handler);
    }
    public onCallRegistered(handler: (data: CallBase) => void) {
        this._addHandler(CRM_ACTIONS.SESSION_REGISTERED, handler);
    }
    public onChatPresented(handler: (data: ChatBase) => void) {
        this._addHandler(CRM_ACTIONS.CHAT_PRESENTED, handler);
    }
    public onChatNewMessageHasGot(handler: (data: ChatBase) => void) {
        this._addHandler(CRM_ACTIONS.CHAT_NEW_MESSAGE, handler);
    }
    public onChatSetStatus(handler: (data: string) => void) {
        this._addHandler(CRM_ACTIONS.CHAT_SET_STATUS, handler);
    }

    public onChatAccepted(handler: (data: ChatBase) => void) {
        this._addHandler(CRM_ACTIONS.CHAT_ACCEPTED, handler);
    }

    public onOpenRecord(handler: (data: OpenRecord) => void) {
        this._addHandler(CRM_ACTIONS.CRM_OPEN_RECORD, handler);
    }

    public onPopRecords(handler: (data: PopRecords) => void) {
        this._addHandler(CRM_ACTIONS.CRM_POP_RECORDS, handler);
    }

    // Generic function on get locale information from RCX to CRM
    public onGetLocaleFromRCX(handler: () => void) {
        this._addHandler(CRM_ACTIONS.GET_CRM_LOCALE, handler);
    }

    public onChatRejected(handler: (data: ChatBase) => void) {
        this._addHandler(CRM_ACTIONS.CHAT_REJECTED, handler);
    }

    public onChatDispResponse(handler: (data: OnChatDispResponse) => void) {
        this._addHandler(CRM_ACTIONS.CHAT_DISP_RESPONSE, handler);
    }

    public onIntegrationHasLogin(handler: (data: any) => void) {
        this._addHandler(CRM_ACTIONS.AGENT_LOGIN, handler);
    }
    public onIntegrationHasLogout(handler: (data: any) => void) {
        this._addHandler(CRM_ACTIONS.AGENT_LOGOUT, handler);
    }

    public onCreateRecord(handler: (data: { type: string; call: any }) => Promise<any>) {
        this._addHandler(CRM_ACTIONS.CRM_CREATE_RECORD, handler);
    }

    public onGetCallLink(handler: (data: OnGetCallLink) => Promise<GetCallLinkResult>) {
        this._addHandler(CRM_ACTIONS.GET_CALL_LINK, handler);
    }

    public onGetChatLink(handler: (data: OnGetChatLink) => Promise<GetChatLinkResult>) {
        this._addHandler(CRM_ACTIONS.GET_CHAT_LINK, handler);
    }

    public onViewLog(handler: (data: any) => void) {
        this._addHandler(CRM_ACTIONS.VIEW_CALL_LOG, handler);
    }

    public onSubmitLog(handler: (data: OnSubmitLog) => Promise<{ success: boolean } | undefined>) {
        this._addHandler(CRM_ACTIONS.SUBMIT_LOG, handler);
    }
    public onUpdateRender(handler: (data: OnUpdateRender) => void) {
        this._addHandler(CRM_ACTIONS.UPDATE_RENDER, handler);
    }
    public onStopRender(handler: (data: OnStopRender) => void) {
        this._addHandler(CRM_ACTIONS.STOP_RENDER, handler);
    }

    public onStartRender(handler: (data: OnStartRender) => void) {
        this._addHandler(CRM_ACTIONS.START_RENDER, handler);
    }

    public onSetRCXMetadata(handler: (data: RCXMetadata) => void) {
        this._addHandler(CRM_ACTIONS.SET_RCX_METADATA, handler);
    }

    // Generic function called on lead selection in the RCX app
    public onLeadSelected(handler: (data: any) => void) {
        this._addHandler(CRM_ACTIONS.LEAD_SELECTED, handler);
    }

    // temporary fix for localstorage issue
    public onStorageError(handler: (data: any) => void) {
        this._addHandler(CRM_ACTIONS.SCRIPT_ON_STORAGE_ERROR, handler);
    }

    public onSendToCpr(handler: (data: any) => void) {
        this._addHandler(CRM_ACTIONS.SEND_TO_CPR, handler);
    }

    public onRCXStateChange(handler: (data: any) => void) {
        this._addHandler(CRM_ACTIONS.STATE_CHANGED, handler);
    }

    public onAcdCallbackReady(handler: (data: any) => void) {
        this._addHandler(CRM_ACTIONS.ACD_CALLBACK_READY, handler);
    }
}
